import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  LuUserPlus,
  LuUserCheck,
  LuShoppingCart,
  LuStore,
} from "react-icons/lu";
import SearchInput from "../../components/search-input";
import AsyncSearchSelect, {
  AsyncOption,
} from "../../components/async-search-select";
import Tabs from "../../components/tab";
import Table from "../../components/table";
import Button from "../../components/button";
import useRequest from "../../components/hooks/use-request";
import { formatDate } from "../../utilities/functions";
import { TailSpin } from "react-loader-spinner";

const avatarColors = [
  "#0979A1",
  "#E85D04",
  "#7B2D8B",
  "#2D6A4F",
  "#D62828",
  "#023E8A",
  "#B5179E",
];
const avatarColor = (name: string) =>
  avatarColors[(name?.charCodeAt(0) || 0) % avatarColors.length];

const UserAvatar = ({ name }: { name: string }) => (
  <div
    className='w-7 h-7 rounded-full flex items-center justify-center text-white text-[11px] font-semibold shrink-0'
    style={{ backgroundColor: avatarColor(name) }}>
    {name?.charAt(0).toUpperCase()}
  </div>
);

const StatCard = ({ icon, iconBg, title, value }: any) => (
  <div className='bg-white rounded-lg border border-gray-100 shadow-sm p-4 flex items-center gap-3'>
    <div
      className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${iconBg}`}>
      {icon}
    </div>
    <div className='min-w-0'>
      <p className='text-[12px] text-gray-500 break-words'>{title}</p>
      <p className='text-[20px] font-bold leading-tight'>{value}</p>
    </div>
  </div>
);

const userTypeBadge: Record<string, string> = {
  buyer: "bg-blue-50 text-blue-600",
  merchant: "bg-purple-50 text-purple-600",
  admin: "bg-orange-50 text-orange-600",
};

const tabs = ["All Reffered Buyers", "Buyers", "Merchants"];
// userType <-> tab mapping so the dropdown and tabs stay in sync
const tabToType: Record<string, string> = {
  "All Reffered Buyers": "",
  Buyers: "buyer",
  Merchants: "merchant",
};
const typeToTab: Record<string, string> = {
  "": "All Reffered Buyers",
  buyer: "Buyers",
  merchant: "Merchants",
};

const ReferredUsers = () => {
  const userToken = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${userToken}` };

  const [users, setUsers] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [userType, setUserType] = useState(""); // "" | buyer | merchant | admin — single source for tabs + dropdown
  const [activationStatus, setActivationStatus] = useState("All");
  const [marketerId, setMarketerId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // "admin" has no matching tab, so no tab is highlighted while it is selected
  const activeTab = typeToTab[userType] ?? "";

  const { makeRequest: getUsers, loading } = useRequest(
    "/marketers/referred-users",
    "GET",
    headers,
  );
  const { makeRequest: getLeaderboard } = useRequest(
    "/marketers/leaderboard",
    "GET",
    headers,
  );

  // Selecting a tab drives the shared userType
  const handleTabChange = (tab: string) => setUserType(tabToType[tab] ?? "");

  // Debounced async source for the Sales Associate dropdown
  const fetchMarketers = async (term: string): Promise<AsyncOption[]> => {
    const [res] = await getLeaderboard(
      undefined,
      term ? { search: term } : undefined,
    );
    const list: any[] = res?.data || [];
    const mapped = list
      .map((m) => ({
        value: m.id || "",
        label: m.name || m.fullName || "",
      }))
      .filter((o) => o.value);
    // Fall back to client-side filtering in case the endpoint ignores `search`
    if (!term) return mapped;
    return mapped.filter((o) =>
      o.label.toLowerCase().includes(term.toLowerCase()),
    );
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [userType, search, marketerId]);

  useEffect(() => {
    const fetch = async () => {
      const params: Record<string, any> = { page: currentPage };
      if (search) params.search = search;
      if (userType) params.userType = userType;
      if (marketerId) params.marketerId = marketerId;
      const [res] = await getUsers(undefined, params);
      if (res?.data) {
        setUsers(res.data.data || []);
        setTotalPages(res.data.totalPages || 1);
      }
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, search, userType, marketerId]);

  const handleApply = () => setCurrentPage(1);

  const handleReset = () => {
    setUserType("");
    setActivationStatus("All");
    setMarketerId("");
    setSearch("");
    setCurrentPage(1);
  };

  const columns = [
    {
      header: "Name / Email",
      accessor: "fullName",
      render: (row: any) => (
        <div className='flex items-center gap-2'>
          <UserAvatar name={row.fullName || ""} />
          <div className='min-w-0'>
            <p className='text-[13px] font-medium'>{row.fullName}</p>
            <p className='text-[11px] text-gray-500'>{row.email}</p>
          </div>
        </div>
      ),
    },
    { header: "Phone", accessor: "phone" },
    {
      header: "User Type",
      accessor: "userType",
      render: (row: any) => (
        <span
          className={`px-2 py-0.5 rounded text-[12px] font-medium capitalize ${userTypeBadge[row.userType?.toLowerCase()] || "bg-gray-100 text-gray-600"}`}>
          {row.userType}
        </span>
      ),
    },
    { header: "Referred By", accessor: "referrer" },
    {
      header: "Activation Status",
      accessor: "activationStatus",
      render: (row: any) => (
        <span
          className={`px-2 py-0.5 rounded text-[12px] font-medium ${row.activationStatus ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
          {row.activationStatus ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      header: "Activated On",
      accessor: "activatedOn",
      render: (row: any) =>
        row.activatedOn ? formatDate(row.activatedOn) : "—",
    },
    {
      header: "Date Joined",
      accessor: "created_at",
      render: (row: any) => (row.created_at ? formatDate(row.created_at) : "—"),
    },
  ];

  return (
    <div className='w-[95%]'>
      <p className='text-[13px] text-gray-500 mb-1'>
        <Link to='/referral-program' className='text-[#0979A1] font-medium'>
          Overview
        </Link>
        {" / Referred Users"}
      </p>
      <h2 className='text-[24px] font-bold mb-5'>Referred Users</h2>

      {/* Stat cards — pending API endpoint */}
      <div className='grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6'>
        <StatCard
          icon={<LuUserPlus size={18} color='#0979A1' />}
          iconBg='bg-[#E8F7FA]'
          title='Total Referred Users'
          value='—'
        />
        <StatCard
          icon={<LuUserCheck size={18} color='#E85D04' />}
          iconBg='bg-orange-50'
          title='Total Activated Users'
          value='—'
        />
        <StatCard
          icon={<LuShoppingCart size={18} color='#7B2D8B' />}
          iconBg='bg-purple-50'
          title='Buyers'
          value='—'
        />
        <StatCard
          icon={<LuStore size={18} color='#2D6A4F' />}
          iconBg='bg-green-50'
          title='Merchants'
          value='—'
        />
      </div>

      <Tabs
        activeTab={activeTab}
        tabs={tabs}
        setActiveTab={handleTabChange}
        variant='underline'
        className='mb-5'
      />

      {/* Filters */}
      <div className='flex flex-wrap items-center gap-3 mb-4'>
        <SearchInput
          placeholder='Search by name, email or phone...'
          onSearch={(val) => setSearch(val)}
          className='w-64'
        />
        <div className='ml-auto flex items-end gap-2 flex-wrap'>
          <div>
            <label className='text-[11px] text-gray-500 block mb-0.5'>
              User Type
            </label>
            <select
              value={userType}
              onChange={(e) => setUserType(e.target.value)}
              className='border border-gray-200 rounded-lg px-3 py-1.5 text-[13px] outline-none'>
              <option value=''>All</option>
              <option value='buyer'>Buyer</option>
              <option value='merchant'>Merchant</option>
              <option value='admin'>Admin</option>
            </select>
          </div>
          <div>
            <label className='text-[11px] text-gray-500 block mb-0.5'>
              Activation Status
            </label>
            <select
              value={activationStatus}
              onChange={(e) => setActivationStatus(e.target.value)}
              className='border border-gray-200 rounded-lg px-3 py-1.5 text-[13px] outline-none'>
              <option>All</option>
              <option value='yes'>Active</option>
              <option value='no'>Inactive</option>
            </select>
          </div>
          <div>
            <label className='text-[11px] text-gray-500 block mb-0.5'>
              Sales Associate
            </label>
            <AsyncSearchSelect
              fetchOptions={fetchMarketers}
              value={marketerId}
              onChange={(opt) => setMarketerId(opt?.value || "")}
              placeholder='All'
              className='w-56'
            />
          </div>
          <div className='flex items-end gap-2'>
            <button
              onClick={handleReset}
              className='px-4 py-1.5 border border-gray-200 rounded-lg text-[13px] hover:bg-gray-50'>
              Reset
            </button>
            <Button
              size='sm'
              variant='primary'
              type='button'
              onClick={handleApply}>
              Apply
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className='flex justify-center items-center h-64'>
          <TailSpin color='skyblue' />
        </div>
      ) : (
        <Table
          columns={columns}
          data={users}
          onUserClick={() => {}}
          selectedUserId={null}
          sn={false}
        />
      )}

      <div className='mt-4 flex justify-between items-center text-[13px] text-gray-600'>
        <span>
          Page {currentPage} of {totalPages}
        </span>
        <div className='flex items-center gap-3'>
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className='px-3 py-1 border border-gray-200 rounded-md disabled:opacity-40 hover:bg-gray-50 text-[13px]'>
            Prev
          </button>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className='px-3 py-1 border border-gray-200 rounded-md disabled:opacity-40 hover:bg-gray-50 text-[13px]'>
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReferredUsers;
